import { useMemo } from 'react';
import { KIND_LABELS, ORIGIN_LABELS, type Category, type Task, type TaskStatus } from '@shared/domain.ts';
import { useNow, useRigg } from '@/data/RiggProvider.tsx';
import { agentPulse, prodDeploy, queueCounts, reviewTimings, type DurationStat } from '@/data/selectors.ts';
import { ago, cn, duration, plural } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';
import { Page, Card } from './Page.tsx';
import { SectionLabel, Shimmer } from '@/components/ui.tsx';

/**
 * The landing view for "how is the project doing", as opposed to Tasks, which
 * answers "what needs me right now".
 *
 * Three questions, in the order people actually ask them: what is there,
 * is production okay, and what has actually shipped. Nothing here is data the
 * rest of the app does not already hold — this is a rollup, not a new source
 * of truth, which is also why it needs no loading state of its own beyond the
 * one the board already has.
 */
export function OverviewScreen() {
  const { data, project, isAdmin } = useRigg();
  const now = useNow();

  const tasks = useMemo(() => Object.values(data.tasks), [data.tasks]);
  const counts = useMemo(() => queueCounts(data, isAdmin, now), [data, isAdmin, now]);
  const prod = prodDeploy(data.ciRuns, project ? data.settings[project.id]?.prod_workflow : undefined);

  const activeNow = useMemo(
    () => tasks.filter((task) => agentPulse(task, now).tone === 'live').length,
    [tasks, now],
  );

  const recent = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const since = (at: string | null) => Boolean(at) && Date.parse(at!) >= cutoff;
    return {
      merged: tasks.filter((t) => since(t.merged_at)).length,
      deployed: tasks.filter((t) => since(t.deployed_at)).length,
    };
  }, [tasks]);

  const timings = useMemo(() => reviewTimings(tasks), [tasks]);

  const stages = useMemo(() => STAGES.map((stage) => ({ ...stage, tasks: stageTasks(tasks, stage) })), [tasks]);
  const categoryRows = useMemo(() => categoryBreakdown(tasks, data.categories), [tasks, data.categories]);
  const kindRows = useMemo(
    () => countBy(tasks, (task) => task.kind, (kind) => (kind ? KIND_LABELS[kind] : 'No kind')),
    [tasks],
  );
  const originRows = useMemo(
    () => countBy(tasks, (task) => task.origin, (origin) => ORIGIN_LABELS[origin]),
    [tasks],
  );

  if (!data.loaded) {
    return (
      <Page title="Overview" blurb="Where the project stands right now." wide>
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((n) => (
            <Shimmer key={n} className="h-24 rounded-2xl" />
          ))}
        </div>
      </Page>
    );
  }

  return (
    <Page title="Overview" blurb="Where the project stands right now." wide>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Total tasks" value={tasks.length} />
        <StatTile label="Needs you" value={counts['needs-you']} tone="accent" />
        <StatTile label="Active now" value={activeNow} />
        <StatTile label="Merged, last 7d" value={recent.merged} />
        <StatTile label="Deployed, last 7d" value={recent.deployed} tone="good" />
      </div>

      <SectionLabel className="mt-8 mb-2.5">Production</SectionLabel>
      <Card>
        {prod.state === 'none' ? (
          <p className="text-[13px] leading-relaxed text-body">
            No production workflow is named yet, so Rigg cannot say when prod last deployed.{' '}
            <a
              href="/settings"
              onClick={(event) => {
                event.preventDefault();
                navigate('/settings');
              }}
            >
              Name one in Settings →
            </a>
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={cn(
                'size-2.5 shrink-0 rounded-full',
                prod.state === 'passing' ? 'bg-goodx' : prod.state === 'failing' ? 'bg-warnx' : 'bg-amberx',
                prod.state === 'running' && 'animate-pulse',
              )}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="text-[14.5px] font-semibold text-hi">
                {prod.state === 'passing'
                  ? 'Production is up to date'
                  : prod.state === 'failing'
                    ? 'Last production deploy failed'
                    : 'Deploying now…'}
              </div>
              {prod.at ? (
                <div className="mt-0.5 text-[12.5px] text-mute">
                  {ago(prod.at, now)} · {new Date(prod.at).toLocaleString()}
                </div>
              ) : null}
            </div>
            {prod.url ? (
              <a href={prod.url} target="_blank" rel="noreferrer" className="shrink-0 text-[12.5px] font-semibold">
                View run →
              </a>
            ) : null}
          </div>
        )}
      </Card>

      <SectionLabel className="mt-8 mb-2.5">Review & merge time</SectionLabel>
      <Card>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <TimingStat
            label="Time to reviewable"
            hint="raised → first preview or screenshot"
            stat={timings.toReviewable}
          />
          <TimingStat label="Time to merge" hint="approved → merged" stat={timings.toMerge} />
        </div>
      </Card>

      <SectionLabel className="mt-8 mb-2.5">Pipeline</SectionLabel>
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-max gap-3">
          {stages.map((stage) => (
            <div key={stage.key} className="w-[196px] shrink-0 rounded-2xl border border-line bg-panel p-3.5">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-t5">
                  <span className={cn('size-1.5 shrink-0 rounded-full', DOT_TONE[stage.tone])} aria-hidden />
                  {stage.label}
                </span>
                <span className="text-[12px] font-semibold text-mute">{stage.tasks.length}</span>
              </div>
              <div className="mt-2.5 flex flex-col gap-1">
                {stage.tasks.length === 0 ? (
                  <span className="px-2 py-1 text-[12px] text-faint">Nothing here</span>
                ) : (
                  stage.tasks.slice(0, 5).map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => navigate(`/t/${task.id}`)}
                      className="rg-focus block w-full cursor-pointer truncate rounded-lg px-2 py-1.5 text-left text-[12.5px] text-t3 hover:bg-navhover hover:text-hi3"
                      title={task.title}
                    >
                      {task.title}
                    </button>
                  ))
                )}
                {stage.tasks.length > 5 ? (
                  <span className="px-2 text-[11px] text-faint">+{stage.tasks.length - 5} more</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-x-4 lg:grid-cols-3">
        <Breakdown title="By category" rows={categoryRows} />
        <Breakdown title="By kind" rows={kindRows} />
        <Breakdown title="By origin" rows={originRows} />
      </div>
    </Page>
  );
}

/* ── stat tiles ──────────────────────────────────────────────────────────── */

function StatTile({
  label,
  value,
  tone = 'plain',
}: {
  label: string;
  value: number;
  tone?: 'plain' | 'accent' | 'good';
}) {
  return (
    <div className="rounded-2xl border border-line bg-panel px-4 py-3.5">
      <div
        className={cn(
          'text-[26px] font-[650] tracking-[-0.01em] tabular-nums',
          tone === 'accent' ? 'text-accent' : tone === 'good' ? 'text-goodx' : 'text-hi',
        )}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[12px] font-semibold text-mute">{label}</div>
    </div>
  );
}

/* ── review & merge timing ───────────────────────────────────────────────── */

function TimingStat({ label, hint, stat }: { label: string; hint: string; stat: DurationStat }) {
  return (
    <div>
      <div className="text-[12.5px] font-semibold text-mute">{label}</div>
      <div className="mt-1 text-[22px] font-[650] tracking-[-0.01em] text-hi tabular-nums">
        {stat.medianMs === null ? '—' : duration(stat.medianMs)}
      </div>
      <div className="mt-0.5 text-[12px] text-faint">
        {stat.count === 0 ? 'No data yet' : `median · ${plural(stat.count, 'task')} · ${hint}`}
      </div>
    </div>
  );
}

/* ── the pipeline, as mutually exclusive stages ─────────────────────────── */

const DOT_TONE: Record<'warn' | 'mute' | 'good', string> = {
  warn: 'bg-warnx',
  mute: 'bg-mute',
  good: 'bg-goodx',
};

/**
 * `error` is not a rung on the ladder — it falls in from anywhere — so it
 * gets its own bucket rather than distorting whichever status a task was in
 * when it broke.
 */
const STAGES: {
  key: string;
  label: string;
  tone: 'warn' | 'mute' | 'good';
  statuses: TaskStatus[];
  dateField: keyof Pick<Task, 'updated_at' | 'merged_at' | 'deployed_at'>;
}[] = [
  { key: 'error', label: 'Failed', tone: 'warn', statuses: ['error'], dateField: 'updated_at' },
  { key: 'working', label: 'Working', tone: 'mute', statuses: ['fresh', 'working'], dateField: 'updated_at' },
  { key: 'signed_off', label: 'Signed off', tone: 'mute', statuses: ['signed_off'], dateField: 'updated_at' },
  { key: 'approved', label: 'Merging', tone: 'mute', statuses: ['approved'], dateField: 'updated_at' },
  { key: 'merged', label: 'Merged', tone: 'mute', statuses: ['merged'], dateField: 'merged_at' },
  { key: 'deployed', label: 'Deployed', tone: 'good', statuses: ['deployed'], dateField: 'deployed_at' },
];

function stageTasks(tasks: Task[], stage: (typeof STAGES)[number]): Task[] {
  return tasks
    // A fire-and-forget handoff ends `deployed` the moment a routine accepts
    // it, with nothing shipped — counted here it would pass for delivered work.
    .filter((task) => task.source !== 'background')
    .filter((task) => (stage.statuses as string[]).includes(task.status))
    .sort((a, b) => {
      const at = a[stage.dateField] ?? a.updated_at;
      const bt = b[stage.dateField] ?? b.updated_at;
      return Date.parse(bt) - Date.parse(at);
    });
}

/* ── breakdowns ──────────────────────────────────────────────────────────── */

/** One bar chart of counts, biggest first — the same shape for every breakdown. */
function Breakdown({ title, rows }: { title: string; rows: CategoryRow[] }) {
  return (
    <div className="min-w-0">
      <SectionLabel className="mb-2.5">{title}</SectionLabel>
      <Card>
        {rows.length === 0 ? (
          <p className="text-[13px] text-mute">No tasks yet.</p>
        ) : (
          <div className="flex flex-col">
            {rows.map((row) => (
              <div key={row.id} className="flex items-center gap-3 py-1.5">
                <span className="flex w-[130px] shrink-0 items-center gap-2 truncate text-[13px] text-t2">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: row.color ?? 'var(--color-mute)' }}
                    aria-hidden
                  />
                  <span className="min-w-0 truncate">{row.label}</span>
                </span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${Math.max(4, (row.count / rows[0]!.count) * 100)}%`,
                      background: row.color ?? 'var(--color-mute)',
                    }}
                  />
                </span>
                <span className="w-8 shrink-0 text-right text-[12.5px] font-semibold text-mute tabular-nums">
                  {row.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/** Counts by one field of the task — absent values counted under their own label. */
function countBy<K extends string | null>(
  tasks: Task[],
  key: (task: Task) => K,
  label: (value: K) => string,
): CategoryRow[] {
  const counts = new Map<K, number>();
  for (const task of tasks) counts.set(key(task), (counts.get(key(task)) ?? 0) + 1);
  return [...counts]
    .map(([value, count]) => ({ id: value ?? '__none', label: label(value), color: null, count }))
    .sort((a, b) => b.count - a.count);
}

interface CategoryRow {
  id: string;
  label: string;
  color: string | null;
  count: number;
}

function categoryBreakdown(tasks: Task[], categories: Record<string, Category>): CategoryRow[] {
  const counts = new Map<string, number>();
  let uncategorized = 0;
  for (const task of tasks) {
    if (task.category_id) counts.set(task.category_id, (counts.get(task.category_id) ?? 0) + 1);
    else uncategorized += 1;
  }

  const rows: CategoryRow[] = Object.values(categories)
    .map((category) => ({
      id: category.id,
      label: category.label,
      color: category.color,
      count: counts.get(category.id) ?? 0,
    }))
    .filter((row) => row.count > 0);

  if (uncategorized > 0) {
    rows.push({ id: '__none', label: 'Uncategorized', color: null, count: uncategorized });
  }

  return rows.sort((a, b) => b.count - a.count);
}
